import React, { createContext, useContext, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOConfig {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  structuredData?: object;
  type?: 'website' | 'article' | 'product';
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
}

interface SEOContextType {
  setPageSEO: (config: SEOConfig) => void;
  defaultSEO: SEOConfig;
}

const SEOContext = createContext<SEOContextType | undefined>(undefined);

interface SEOProviderProps {
  children: ReactNode;
  defaultConfig?: Partial<SEOConfig>;
}

const DEFAULT_SEO: SEOConfig = {
  title: 'Assurance Dommages-Ouvrage | Guide complet et devis DO',
  description: 'Guide complet sur l\'assurance Dommages-Ouvrage : obligation légale, tarifs, procédures. Obtenez votre devis DO personnalisé gratuitement.',
  canonical: 'https://assurancedo.fr/',
  ogImage: 'https://assurancedo.fr/hero-assurance-do.jpg',
  type: 'website',
  keywords: ['assurance dommages-ouvrage', 'DO', 'construction', 'maîtrise d\'ouvrage', 'garantie décennale'],
  author: 'AssuranceDO.fr'
};

export const SEOProvider: React.FC<SEOProviderProps> = ({ children, defaultConfig = {} }) => {
  const defaultSEO = { ...DEFAULT_SEO, ...defaultConfig };

  const setPageSEO = (config: SEOConfig) => {
    // This will be handled by individual components using useSEO hook
  };

  return (
    <SEOContext.Provider value={{ setPageSEO, defaultSEO }}>
      {children}
    </SEOContext.Provider>
  );
};

export const useSEO = () => {
  const context = useContext(SEOContext);
  if (!context) {
    throw new Error('useSEO must be used within a SEOProvider');
  }
  return context;
};

interface SEOHeadProps extends SEOConfig {
  siteName?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonical,
  ogImage,
  structuredData,
  type = 'website',
  keywords = [],
  author,
  publishedTime,
  modifiedTime,
  section,
  siteName = 'AssuranceDO.fr'
}) => {
  const { defaultSEO } = useSEO();
  
  const finalTitle = title || defaultSEO.title;
  const finalDescription = description || defaultSEO.description;
  const finalCanonical = canonical || defaultSEO.canonical;
  const finalOgImage = ogImage || defaultSEO.ogImage;
  const finalKeywords = keywords.length > 0 ? keywords : defaultSEO.keywords;
  const finalAuthor = author || defaultSEO.author;

  const baseStructuredData = {
    "@context": "https://schema.org",
    "@type": type === 'article' ? 'Article' : 'WebPage',
    "name": finalTitle,
    "description": finalDescription,
    "url": finalCanonical,
    "author": {
      "@type": "Organization",
      "name": finalAuthor
    },
    "publisher": {
      "@type": "Organization",
      "name": siteName,
      "url": "https://assurancedo.fr/"
    }
  };

  if (type === 'article' && publishedTime) {
    Object.assign(baseStructuredData, {
      "datePublished": publishedTime,
      "dateModified": modifiedTime || publishedTime,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": finalCanonical
      }
    });
  }

  const finalStructuredData = structuredData ? 
    [baseStructuredData, structuredData] : 
    baseStructuredData;

  return (
    <Helmet>
      {/* Title and Description */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      
      {/* Keywords */}
      {finalKeywords && finalKeywords.length > 0 && (
        <meta name="keywords" content={finalKeywords.join(', ')} />
      )}
      
      {/* Author */}
      {finalAuthor && <meta name="author" content={finalAuthor} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={finalCanonical} />
      
      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:site_name" content={siteName} />
      {finalOgImage && <meta property="og:image" content={finalOgImage} />}
      
      {/* Article specific OG tags */}
      {type === 'article' && publishedTime && (
        <>
          <meta property="article:published_time" content={publishedTime} />
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {finalAuthor && <meta property="article:author" content={finalAuthor} />}
          {section && <meta property="article:section" content={section} />}
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      {finalOgImage && <meta name="twitter:image" content={finalOgImage} />}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
      
      {/* Additional meta tags */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
    </Helmet>
  );
};